/**
 * The Overview's charts, as plain functions from the response to the kit.
 *
 * ── WHAT LIVES HERE AND WHAT DOES NOT ──
 * These decide WHICH series a chart gets, in WHAT order, and what the
 * summary sentence says. They do not parse money: every amount goes through
 * the `minor` function the component hands in, which is `@fint/money`'s
 * `parse` bound to the response's currency. Keeping `@fint/money` out is
 * what lets this file run under Node's test runner (see `overview-rows.ts`
 * for the same reason), and it means a test can hand in a fake that turns
 * "12.34" into 1234 and check the shapes without a browser.
 *
 * ── THE CONTRACT ──
 * GET /v1/practice/overview/charts. Money is decimal STRINGS. A book this
 * person may not report on comes back `withheld: true` with NULL in every
 * figure and a caveat naming it; it is a book, not a missing book, and it
 * is named under the chart it is missing from rather than drawn as zeros.
 */

export interface ChartsMonth {
  month: string;
  income: string;
  expense: string;
  profit: string;
  cumulativeProfit: string;
  betweenBooks: { income: string; expense: string };
}

export interface ChartsSlice {
  key: string;
  name: string;
  emoji: string | null;
  colour: string;
  amount: string;
  share: number;
}

export interface ChartsBook {
  orgId: string;
  name: string;
  colour: string;
  currency: string;
  withheld: boolean;
  inTotals: boolean;
  months: ChartsMonth[] | null;
  topExpenses: ChartsSlice[] | null;
  topIncome: ChartsSlice[] | null;
  cash: { total: string; accounts: number } | null;
  receivables: string | null;
  overdue: string | null;
}

export interface ChartsResponse {
  period: { from: string; to: string };
  basis: 'accrual' | 'cash';
  currency: string;
  months: string[];
  books: ChartsBook[];
  totals: {
    months: ChartsMonth[];
    topExpenses: ChartsSlice[];
    topIncome: ChartsSlice[];
    cash: { total: string; accounts: number };
    receivables: string;
    overdue: string;
    betweenBooks: { income: string; expense: string };
  };
  caveats: string[];
}

/** `@fint/money`'s `parse(...).minor`, bound to the response's currency by the component. */
export type Minor = (amount: string) => number;

/** Whether a response has the shape the charts read. A mocked or older body must fall to the error state, not throw mid-render. */
export function isChartsResponse(value: unknown): value is ChartsResponse {
  if (!value || typeof value !== 'object') return false;
  const body = value as Partial<ChartsResponse>;
  return Array.isArray(body.books) && Array.isArray(body.months) && !!body.totals && Array.isArray(body.totals.months);
}

/** The books that are drawn in the money charts: figured, and in the response's currency. */
export function countedBooks(data: ChartsResponse): ChartsBook[] {
  return data.books.filter((book) => !book.withheld && book.inTotals);
}

/** A month's figure for a book, or zero when the book has no row for it. */
function monthOf(book: ChartsBook, month: string): ChartsMonth | undefined {
  return book.months?.find((row) => row.month === month);
}

// ── (a) money in and out ──────────────────────────────────────────────────

export interface BarSeriesShape {
  id: string;
  name: string;
  colour: string | null;
  values: number[];
}

export interface BarGroupShape {
  id: string;
  name: string;
  short: string;
  hatch?: boolean;
  series: BarSeriesShape[];
}

export interface BarsShape {
  months: string[];
  groups: BarGroupShape[];
  line: { name: string; colour: string | null; values: number[] };
}

/** The line every mode draws: the net profit across books, month by month. */
function netProfitLine(data: ChartsResponse, minor: Minor): BarsShape['line'] {
  return {
    name: 'Net profit, all books',
    colour: 'foreground',
    values: data.months.map((month) => minor(data.totals.months.find((row) => row.month === month)?.profit ?? '0')),
  };
}

/**
 * Per book: one stack per month for money in and one for money out, each
 * book's own figures, so the stacks include money that moved between
 * books - the caption under the chart says the line does not.
 */
export function perBookBars(data: ChartsResponse, minor: Minor): BarsShape {
  const books = countedBooks(data);
  const series = (side: 'income' | 'expense'): BarSeriesShape[] =>
    books.map((book) => ({
      id: book.orgId,
      name: book.name,
      colour: book.colour,
      values: data.months.map((month) => minor(monthOf(book, month)?.[side] ?? '0')),
    }));
  return {
    months: data.months,
    groups: [
      { id: 'in', name: 'Money in', short: 'in', series: series('income') },
      { id: 'out', name: 'Money out', short: 'out', hatch: true, series: series('expense') },
    ],
    line: netProfitLine(data, minor),
  };
}

/** Combined: one bar per month each way, NET of money between books - the Total row spread over the months. */
export function combinedBars(data: ChartsResponse, minor: Minor): BarsShape {
  const values = (side: 'income' | 'expense') =>
    data.months.map((month) => minor(data.totals.months.find((row) => row.month === month)?.[side] ?? '0'));
  return {
    months: data.months,
    groups: [
      { id: 'in', name: 'Money in', short: 'in', series: [{ id: 'all', name: 'All books, net', colour: null, values: values('income') }] },
      { id: 'out', name: 'Money out', short: 'out', hatch: true, series: [{ id: 'all', name: 'All books, net', colour: null, values: values('expense') }] },
    ],
    line: netProfitLine(data, minor),
  };
}

// ── (b) profit, running ───────────────────────────────────────────────────

export interface LineShape {
  id: string;
  name: string;
  colour: string | null;
  values: number[];
  width?: number;
  area?: boolean;
}

/**
 * The running net profit across books as the heavy washed line, and each
 * book's own as a thin one when asked. The combined line is drawn in the
 * theme's ink, as the net line on the bars is: the first book a person
 * makes is blue, and so is the theme's primary, and two blue lines on one
 * chart would leave the legend as the only thing telling them apart.
 */
export function runningLines(data: ChartsResponse, minor: Minor, eachBook: boolean): LineShape[] {
  const combined: LineShape = {
    id: 'all',
    name: 'All books, net',
    colour: 'foreground',
    width: 2,
    area: true,
    values: data.months.map((month) => minor(data.totals.months.find((row) => row.month === month)?.cumulativeProfit ?? '0')),
  };
  if (!eachBook) return [combined];
  const books = countedBooks(data).map(
    (book): LineShape => ({
      id: book.orgId,
      name: book.name,
      colour: book.colour,
      width: 1.25,
      values: data.months.map((month) => minor(monthOf(book, month)?.cumulativeProfit ?? '0')),
    }),
  );
  return [combined, ...books];
}

// ── (c) the rings ─────────────────────────────────────────────────────────

export interface SliceShape {
  key: string;
  name: string;
  emoji: string | null;
  colour: string;
  minor: number;
  share: number;
}

/** The slices for one ring: every counted book together, or one book alone. */
export function ringSlices(data: ChartsResponse, side: 'expense' | 'income', bookId: 'all' | string, minor: Minor): SliceShape[] {
  const source =
    bookId === 'all'
      ? side === 'expense'
        ? data.totals.topExpenses
        : data.totals.topIncome
      : (side === 'expense'
          ? data.books.find((book) => book.orgId === bookId)?.topExpenses
          : data.books.find((book) => book.orgId === bookId)?.topIncome) ?? [];
  return source.map((slice) => ({
    key: slice.key,
    name: slice.name,
    emoji: slice.emoji,
    colour: slice.colour,
    minor: minor(slice.amount),
    share: slice.share,
  }));
}

/** The chips over the rings: all books, then each book that has a ring. */
export function ringChoices(data: ChartsResponse): Array<{ id: 'all' | string; name: string; colour: string | null }> {
  return [{ id: 'all', name: 'All books', colour: null }, ...countedBooks(data).map((book) => ({ id: book.orgId, name: book.name, colour: book.colour }))];
}

// ── (d) cash and receivables ──────────────────────────────────────────────

export interface HBarShape {
  id: string;
  name: string;
  colour: string | null;
  value: number;
  part?: number;
}

export function cashRows(data: ChartsResponse, minor: Minor): HBarShape[] {
  return countedBooks(data).map((book) => ({
    id: book.orgId,
    name: book.name,
    colour: book.colour,
    value: minor(book.cash?.total ?? '0'),
  }));
}

export function owedRows(data: ChartsResponse, minor: Minor): HBarShape[] {
  return countedBooks(data).map((book) => ({
    id: book.orgId,
    name: book.name,
    colour: book.colour,
    value: minor(book.receivables ?? '0'),
    part: minor(book.overdue ?? '0'),
  }));
}

// ── what is missing, and what was taken out ───────────────────────────────

/** The books a chart cannot draw, each with why, for the line under it. */
export function missingBooks(data: ChartsResponse): Array<{ name: string; reason: 'withheld' | 'currency' }> {
  return data.books
    .filter((book) => book.withheld || !book.inTotals)
    .map((book) => ({ name: book.name, reason: book.withheld ? 'withheld' : 'currency' }));
}

/** The larger of the two between-books halves in minor units, or zero when nothing moved. */
export function betweenBooksMinor(data: ChartsResponse, minor: Minor): number {
  return Math.max(minor(data.totals.betweenBooks.income), minor(data.totals.betweenBooks.expense));
}

// ── what a month's readout says beyond its bars ───────────────────────────

/**
 * The net profit of each month, in minor units: the step the running line takes.
 *
 * `profit` as the response gives it, NOT income minus expense - the server has
 * already taken out what the books paid each other, and subtracting the two
 * totals here would count an inter-book transfer on both sides and disagree
 * with the line drawn above it.
 *
 * Looked up by month rather than by position, as `combinedBars` does: the
 * charts are indexed by `data.months`, and the totals series is its own array.
 * A month the totals do not carry is a month of nothing, not an undefined.
 */
export function monthlyNet(data: ChartsResponse, minor: Minor): number[] {
  return data.months.map((month) => minor(data.totals.months.find((row) => row.month === month)?.profit ?? '0'));
}

/**
 * How many of the drawn books had anything in this month, of how many there are.
 *
 * The per-book charts answer "how many records are behind this bar"; a month
 * across books cannot - the response carries sums, not counts - and "2 of 3
 * books" is the version of that question this page can answer honestly. It is
 * also the one that changes a reader's mind: a good month that was one book is
 * a different fact from a good month that was all of them.
 *
 * A withheld book is in neither number. Whether it moved is not ours to say.
 */
export function booksMoving(data: ChartsResponse, month: string, minor: Minor): { moved: number; of: number } {
  const books = countedBooks(data);
  const moved = books.filter((book) => {
    const row = monthOf(book, month);
    return !!row && (minor(row.income) !== 0 || minor(row.expense) !== 0);
  }).length;
  return { moved, of: books.length };
}

/** Whether the period has any money movement at all, across the counted books. */
export function anyMovement(data: ChartsResponse, minor: Minor): boolean {
  return data.totals.months.some((row) => minor(row.income) !== 0 || minor(row.expense) !== 0) ||
    countedBooks(data).some((book) => book.months?.some((row) => minor(row.income) !== 0 || minor(row.expense) !== 0) ?? false);
}

// ── the sentences ─────────────────────────────────────────────────────────

const plural = (n: number, one: string, many: string) => `${n} ${n === 1 ? one : many}`;

/** One sentence for the picture: what it covers, and the totals a reader would want first. */
export function summarise(
  data: ChartsResponse,
  what: string,
  figures: string,
): string {
  const books = countedBooks(data).length;
  return `${what} across ${plural(books, 'book', 'books')} over ${plural(data.months.length, 'month', 'months')}, in ${data.currency}: ${figures}.`;
}
